import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import { Edit, RefreshCw, Download, Building2 } from 'lucide-react';
import { API_BASE_URL } from '../../utils/apiConfig';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

interface EditProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectName: string | null;
    onSaveSuccess: () => void;
}

export const EditProjectModal: React.FC<EditProjectModalProps> = ({
    isOpen, onClose, projectName, onSaveSuccess
}) => {
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);

    useEffect(() => {
        const loadProject = async () => {
            if (!isOpen || !projectName) return;
            setLoading(true);
            try {
                const projectData = await authenticatedFetchJSON(`${API_BASE_URL}/api/projects/load/${encodeURIComponent(projectName)}`);

                setEditFormData({
                    displayName: projectData.projectMetadata?.displayName || projectName,
                    customer: projectData.projectMetadata?.customer || '',
                    status: projectData.projectMetadata?.status || 'active',
                    stage: projectData.projectMetadata?.stage || 'concept',
                    description: projectData.projectMetadata?.description || '',
                    fullData: projectData, // Keep ref to full data for saving
                    isNewCustomerMode: false
                });
            } catch (error) {
                console.error("Failed to load project for editing", error);
                alert("Failed to load project details.");
                onClose();
            } finally {
                setLoading(false);
            }
        };
        loadProject();
    }, [isOpen, projectName]);

    const handleSave = async () => {
        if (!editFormData || !projectName) return;
        setIsSaving(true);

        try {
            const updatedData = { ...editFormData.fullData };
            if (!updatedData.projectMetadata) updatedData.projectMetadata = {};

            updatedData.projectMetadata.displayName = editFormData.displayName;
            updatedData.projectMetadata.customer = editFormData.customer;
            updatedData.projectMetadata.status = editFormData.status;
            updatedData.projectMetadata.stage = editFormData.stage;
            updatedData.projectMetadata.description = editFormData.description;
            updatedData.projectMetadata.lastModified = new Date().toISOString();

            await authenticatedFetchJSON('/api/projects/save', {
                method: 'POST',
                body: JSON.stringify(updatedData)
            });

            alert(`✅ Project "${projectName}" updated successfully!`);
            onSaveSuccess();
            onClose();
        } catch (error: any) {
            console.error('Update failed:', error);
            alert(`❌ Error updating project: ${error.message || error}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg animate-in zoom-in-95 duration-200">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Edit className="text-primary" /> Edit Project Details
                </h2>

                {loading || !editFormData ? (
                    <div className="flex justify-center p-8">
                        <RefreshCw className="animate-spin text-primary" size={32} />
                    </div>
                ) : (
                    <>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Display Name</label>
                                <input
                                    type="text"
                                    value={editFormData.displayName}
                                    onChange={(e) => setEditFormData({ ...editFormData, displayName: e.target.value })}
                                    className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Customer / Client</label>
                                {editFormData.isNewCustomerMode ? (
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" size={16} />
                                            <input
                                                type="text"
                                                autoFocus
                                                value={editFormData.customer}
                                                onChange={(e) => setEditFormData({ ...editFormData, customer: e.target.value })}
                                                className="w-full pl-9 pr-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                                                placeholder="Enter new customer name"
                                            />
                                        </div>
                                        <button
                                            onClick={() => setEditFormData({ ...editFormData, isNewCustomerMode: false, customer: '' })}
                                            className="px-3 py-2 border border-border rounded hover:bg-muted text-text-secondary"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" size={16} />
                                        <input
                                            type="text"
                                            value={editFormData.customer}
                                            onChange={(e) => setEditFormData({ ...editFormData, customer: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                                            placeholder="Customer Name"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
                                    <select
                                        value={editFormData.status}
                                        onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                                        className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Stage</label>
                                    <select
                                        value={editFormData.stage}
                                        onChange={(e) => setEditFormData({ ...editFormData, stage: e.target.value })}
                                        className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                                    >
                                        <option value="concept">Concept</option>
                                        <option value="design">Design</option>
                                        <option value="prototype">Prototype</option>
                                        <option value="production">Production</option>
                                        <option value="maintenance">Maintenance</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                                <textarea
                                    value={editFormData.description}
                                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary bg-background min-h-[100px]"
                                    placeholder="Enter project description..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 border border-border rounded hover:bg-muted text-sm font-medium"
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm font-medium shadow-sm flex items-center gap-2"
                            >
                                {isSaving ? (
                                    <>
                                        <RefreshCw size={14} className="animate-spin" /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <Download size={14} /> Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};
